import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";

import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import { getCourses, getLessons, type Course } from "@/lib/training-api";

interface CourseWithProgress extends Course {
  total: number;
  completed: number;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; courses: CourseWithProgress[] }
  | { kind: "error" };

export default function CoursesPage() {
  const { t } = useTranslation();
  const [loadState, setLoadState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ courses }, { lessons }] = await Promise.all([getCourses(), getLessons()]);
        if (cancelled) return;
        const completedById = new Map(lessons.map((l) => [l.id, l.completed]));
        const withProgress = courses.map((c) => ({
          ...c,
          total: c.lesson_ids.length,
          completed: c.lesson_ids.filter((id) => completedById.get(id)).length,
        }));
        setLoadState({ kind: "ready", courses: withProgress });
      } catch {
        if (!cancelled) setLoadState({ kind: "error" });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <LearnerPortalShell pageTitle={t("nav.courses")}>
      <div className="bg-slate-50 min-h-full">
        {/* Hero */}
        <div className="bg-gradient-to-br from-brand-primary via-slate-800 to-slate-900 text-white">
          <div className="mx-auto max-w-6xl px-4 py-12 sm:px-8 sm:py-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-slate-400">
              {t("courses.hero.eyebrow")}
            </p>
            <h1 className="mt-2 font-display text-4xl font-bold tracking-tight sm:text-5xl">
              {t("courses.hero.title")}
            </h1>
            <p className="mt-4 max-w-xl text-base text-slate-300">{t("courses.hero.subtitle")}</p>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
          {loadState.kind === "loading" && (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-48 animate-pulse rounded-2xl bg-slate-200" />
              ))}
            </div>
          )}

          {loadState.kind === "error" && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-600">
              {t("courses.error")}
            </div>
          )}

          {loadState.kind === "ready" && loadState.courses.length === 0 && (
            <div className="py-24 text-center text-slate-500">{t("courses.empty")}</div>
          )}

          {loadState.kind === "ready" && loadState.courses.length > 0 && (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {loadState.courses.map((c) => (
                <CourseCard key={c.id} course={c} />
              ))}
            </div>
          )}
        </div>
      </div>
    </LearnerPortalShell>
  );
}

function CourseCard({ course }: { course: CourseWithProgress }) {
  const { t } = useTranslation();
  const pct = course.total > 0 ? Math.round((course.completed / course.total) * 100) : 0;
  return (
    <Link
      to={`/courses/${course.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md no-underline"
    >
      <div className="relative flex h-32 items-center justify-center bg-gradient-to-br from-brand-primary/90 to-slate-800 text-5xl">
        {course.emoji ?? "📚"}
        {course.locked && (
          <span className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-amber-700 shadow">
            <Lock aria-hidden="true" size={14} />
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="font-semibold text-slate-900 leading-snug group-hover:text-brand-primary transition-colors">
          {course.title}
        </h3>
        <p className="text-sm text-slate-500 line-clamp-2">{course.summary}</p>
        <div className="mt-auto flex items-center justify-between pt-2 text-xs">
          <span className="text-slate-400">
            {t("courses.card.lessonCount", { count: course.total })}
          </span>
          <span className="font-semibold text-brand-primary">
            {t("courses.card.progress", { completed: course.completed, total: course.total })}
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-brand-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </Link>
  );
}
